import {useNavigate, useParams} from 'react-router-dom'
import {TextValidator, ValidatorForm} from "react-material-ui-form-validator"
import Button from "@mui/material/Button"
import Box from "@mui/material/Box"
import React, {useEffect, useRef, useState} from "react"
import {getFieldErrorParams, getInputValue} from "../../../utils"
import AdminContainer from "../../../components/admin/AdminContainer"
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  Typography,
  Chip,
  Tabs,
  Tab,
  Divider
} from "@mui/material"
import {useGlobalContext} from "../../../contexts/global_context"
import {BACKEND_URL, BRAND_NAME} from "../../../data/config"
import {Helmet} from "react-helmet"

const SettingsAdminPage = () => {
  const navigate = useNavigate()
  let {uid} = useParams()
  let form = useRef()
  let {sendAjax} = useGlobalContext()
  let [fieldErrors, setFieldErrors] = useState({})
  let [socialClients, setSocialClients] = useState([])
  let [tiktokClients, setTiktokClients] = useState([])
  let [activeTab, setActiveTab] = useState(0)

  useEffect(() => {
    // Load settings
    sendAjax({
      url: `${BACKEND_URL}/api/v1/core/admin/settings/`,
      method: 'get',
      data: {},
    }).then((r) => {
      if (r.data.status_code === 200) {
        let settings = r.data.data
        setFormValues({
          scraper_proxies: settings.scraper_proxies.join('\n'),
        })
      }
    })

    // Load Instagram social clients
    loadSocialClients()

    // Load TikTok social clients
    loadTikTokClients()
  }, [])

  const loadSocialClients = () => {
    sendAjax({
      url: `${BACKEND_URL}/api/v1/socials/admin/clients/?social=10`,
      method: 'get',
      data: {},
    }).then((r) => {
      if (r.data.status_code === 200) {
        // ListAPIView returns paginated results
        setSocialClients(r.data.data.results || r.data.data)
      }
    })
  }

  const loadTikTokClients = () => {
    sendAjax({
      url: `${BACKEND_URL}/api/v1/socials/admin/clients/?social=20`,
      method: 'get',
      data: {},
    }).then((r) => {
      if (r.data.status_code === 200) {
        setTiktokClients(r.data.data.results || r.data.data)
      }
    })
  }

  let [formValues, setFormValues] = useState({
    scraper_proxies: '',
  })

  const afterSubmit = (r) => {
    if (r.data.status_code === 200) {
      navigate("/admin/settings")
    } else if (r.data.status_code === 400) {
      setFieldErrors(r.data.data)
    }
  }

  const handleSubmit = () => {
    let proxies = []
    formValues.scraper_proxies.split('\n').forEach(proxy => {
      let trimmedProxy = proxy.trim().replace(' ', '')
      if (trimmedProxy) {
        proxies.push(trimmedProxy)
      }
    })

    sendAjax({
      url: `${BACKEND_URL}/api/v1/core/admin/settings/`,
      method: 'post',
      data: {
        ...formValues,
        scraper_proxies: proxies,
      },
    }).then(
      afterSubmit
    )
  }

  const handleChange = (event) => {
    setFormValues({...formValues, [event.target.name]: getInputValue(event.target)})
    setFieldErrors({})
  }

  const handleClientChange = (uid, field, value, isTikTok = false) => {
    const clients = isTikTok ? tiktokClients : socialClients
    sendAjax({
      url: `${BACKEND_URL}/api/v1/socials/admin/clients/${uid}/`,
      method: 'put',
      data: {
        status: field === 'status' ? value : clients.find(c => c.uid === uid)?.status,
        priority: field === 'priority' ? value : clients.find(c => c.uid === uid)?.priority,
      },
    }).then((r) => {
      if (isTikTok) {
        loadTikTokClients()
      } else {
        loadSocialClients()
      }
    })
  }

  const renderMiniChart = (recentTests) => {
    if (!recentTests || recentTests.length === 0) {
      return <Box sx={{display: 'flex', gap: 0.5}}>
        {[...Array(10)].map((_, i) => (
          <Box key={i} sx={{width: 8, height: 20, bgcolor: '#e0e0e0', borderRadius: 0.5}} />
        ))}
      </Box>
    }

    // Reverse to show oldest first (left to right)
    const tests = [...recentTests].reverse()
    const emptySlots = 10 - tests.length

    return (
      <Box sx={{display: 'flex', gap: 0.5}}>
        {[...Array(emptySlots)].map((_, i) => (
          <Box key={`empty-${i}`} sx={{width: 8, height: 20, bgcolor: '#e0e0e0', borderRadius: 0.5}} />
        ))}
        {tests.map((test, i) => (
          <Box
            key={i}
            sx={{
              width: 8,
              height: 20,
              bgcolor: test.result === 10 ? '#4caf50' : '#f44336',
              borderRadius: 0.5
            }}
          />
        ))}
      </Box>
    )
  }

  return (
    <AdminContainer>
      <Helmet>
        <title>Settings - {BRAND_NAME}</title>
      </Helmet>

      <Box>
        {/* API Clients Tabs */}
        <Paper sx={{mb: 4}}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label="Instagram Clients" />
            <Tab label="TikTok Clients" />
          </Tabs>

          {/* Instagram Clients Tab */}
          {activeTab === 0 && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Client Name</TableCell>
                    <TableCell>Health</TableCell>
                    <TableCell>Last 10 Requests</TableCell>
                    <TableCell>Success Rate</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Priority</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {socialClients.map((client) => (
                    <TableRow key={client.uid}>
                      <TableCell>{client.client_name_display}</TableCell>
                      <TableCell>
                        {client.status === 10 && client.is_healthy !== null ? (
                          <Chip
                            label={client.is_healthy ? 'Healthy' : 'Unhealthy'}
                            size="small"
                            color={client.is_healthy ? 'success' : 'error'}
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {client.status === 10 && client.total_tests > 0 ? renderMiniChart(client.recent_tests) : '-'}
                      </TableCell>
                      <TableCell>
                        {client.status === 10 && client.total_tests > 0 ? (
                          <Chip
                            label={`${client.success_rate}%`}
                            size="small"
                            color={client.success_rate >= 90 ? 'success' : client.success_rate >= 70 ? 'warning' : 'error'}
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={client.status}
                            onChange={(e) => handleClientChange(client.uid, 'status', e.target.value)}
                          >
                            <MenuItem value={10}>Active</MenuItem>
                            <MenuItem value={20}>Inactive</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={client.priority}
                            onChange={(e) => handleClientChange(client.uid, 'priority', e.target.value)}
                          >
                            <MenuItem value={10}>Primary</MenuItem>
                            <MenuItem value={20}>Secondary</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* TikTok Clients Tab */}
          {activeTab === 1 && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Client Name</TableCell>
                    <TableCell>Health</TableCell>
                    <TableCell>Last 10 Requests</TableCell>
                    <TableCell>Success Rate</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Priority</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tiktokClients.map((client) => (
                    <TableRow key={client.uid}>
                      <TableCell>{client.client_name_display}</TableCell>
                      <TableCell>
                        {client.status === 10 && client.is_healthy !== null ? (
                          <Chip
                            label={client.is_healthy ? 'Healthy' : 'Unhealthy'}
                            size="small"
                            color={client.is_healthy ? 'success' : 'error'}
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {client.status === 10 && client.total_tests > 0 ? renderMiniChart(client.recent_tests) : '-'}
                      </TableCell>
                      <TableCell>
                        {client.status === 10 && client.total_tests > 0 ? (
                          <Chip
                            label={`${client.success_rate}%`}
                            size="small"
                            color={client.success_rate >= 90 ? 'success' : client.success_rate >= 70 ? 'warning' : 'error'}
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={client.status}
                            onChange={(e) => handleClientChange(client.uid, 'status', e.target.value, true)}
                          >
                            <MenuItem value={10}>Active</MenuItem>
                            <MenuItem value={20}>Inactive</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={client.priority}
                            onChange={(e) => handleClientChange(client.uid, 'priority', e.target.value, true)}
                          >
                            <MenuItem value={10}>Primary</MenuItem>
                            <MenuItem value={20}>Secondary</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Other Settings */}
        <Box
          component={ValidatorForm}
          ref={form}
          onSubmit={handleSubmit}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} sm={12} md={12}>
              <Divider sx={{padding: 2}}>
                <Chip label="Other Settings" size="small"/>
              </Divider>

              <TextValidator
                label={'Scraper Proxies'}
                name={'scraper_proxies'}
                placeholder={
                  'Warning: If you don\'t put proxies here, the system will get ' +
                  'Instagram and TikTok photos without any proxy (Not Recommended)\n' +
                  'Please put proxies here in separated lines like this:\n' +
                  '130.180.231.128:8270:firkgld:eirikdXkv\n' +
                  '45.196.54.142:6721:firkgld:firkgld'
                }
                variant={'outlined'}
                fullWidth={true}
                multiline={true}
                rows={10}
                onChange={handleChange}
                value={formValues.scraper_proxies}
                {...getFieldErrorParams(fieldErrors, 'scraper_proxies')}
                InputProps={{
                  startAdornment: <></>,
                }}
              />
            </Grid>
          </Grid>

          <Button
            color={'primary'}
            type={'submit'}
            variant="contained"
            sx={{
              margin: '20px 0 20px 0',
            }}
            onClick={() => {
            }}
          >
            Save
          </Button>
        </Box>
      </Box>
    </AdminContainer>
  )
}

export default SettingsAdminPage
